import { MusicalNote } from "../MusicalNote";
import { TimingInfo } from "./MasterComposer";
import { BassPattern } from "./PatternInterfaces";

/**
 * Simple root-focused bass pattern.
 * Always hits the root on downbeats, uses scale and chord tones.
 */
export class RootFocusedBassPattern implements BassPattern {
  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    // This pattern operates over a 2-bar (8-beat) cycle
    const beat = timingInfo.beatInPhrase % 8;
    const notes: MusicalNote[] = [];

    // Always hit the root on downbeats (C2)
    notes.push({
      startBeat: 0,
      durationBeats: 0.3, // Sustain most of the beat
      noteNumber: 36, // C2
      velocity: 0.8 + intensity * 0.2,
    });
    notes.push({
      startBeat: 0.5,
      durationBeats: 0.45, // Sustain most of the beat
      noteNumber: 36, // C2
      velocity: 0.4 + intensity * 0.2,
    });

    return notes;
  }
}

/**
 * Walking bass pattern with more movement.
 */
export class WalkingBassPattern implements BassPattern {
  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const beat = timingInfo.beatInPhrase % 8; // 2-bar pattern
    const notes: MusicalNote[] = [];

    switch (beat) {
      case 0:
      case 4:
        // Root on downbeats (C2)
        notes.push({
          startBeat: 0,
          durationBeats: 0.6,
          noteNumber: 36, // C2
          velocity: 0.8,
        });
        break;
      case 1:
      case 5:
        // Scale tones
        notes.push({
          startBeat: 0,
          durationBeats: 0.4,
          noteNumber: 38, // D2
          velocity: 0.7,
        });
        if (intensity > 0.5) {
          notes.push({
            startBeat: 0.5,
            durationBeats: 0.3,
            noteNumber: 40, // E2
            velocity: 0.6,
          });
        }
        break;
      case 2:
      case 6:
        // Chord tones
        notes.push({
          startBeat: 0,
          durationBeats: 0.5,
          noteNumber: 43, // G2
          velocity: 0.7,
        });
        break;
      case 3:
      case 7:
        // Leading back to root
        notes.push({
          startBeat: 0,
          durationBeats: 0.3,
          noteNumber: 35, // B1
          velocity: 0.6,
        });
        if (intensity > 0.7) {
          notes.push({
            startBeat: 0.75,
            durationBeats: 0.2,
            noteNumber: 34, // A#1 (chromatic approach)
            velocity: 0.5,
          });
        }
        break;
    }

    return notes;
  }
}

/**
 * Simple bass fill pattern.
 * Builds back to the root for the next groove section.
 */
export class SimpleBassPattern implements BassPattern {
  execute(timingInfo: TimingInfo, intensity: number): MusicalNote[] {
    const notes: MusicalNote[] = [];

    // Simple ascending scale line back to root
    switch (timingInfo.beatInPhrase % 4) {
      case 0:
        notes.push({
          startBeat: 0,
          durationBeats: 0.4,
          noteNumber: 31, // G1
          velocity: 0.6,
        });
        break;
      case 1:
        notes.push({
          startBeat: 0,
          durationBeats: 0.4,
          noteNumber: 33, // A1
          velocity: 0.7,
        });
        break;
      case 2:
        notes.push({
          startBeat: 0,
          durationBeats: 0.4,
          noteNumber: 35, // B1
          velocity: 0.8,
        });
        break;
      case 3:
        // Build up to root for next section
        notes.push({
          startBeat: 0.5,
          durationBeats: 0.4,
          noteNumber: 36, // C2 (strong entrance)
          velocity: 0.9,
        });
        break;
    }

    return notes;
  }
}
